'use client';

import './header.scss';

import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce, throttle } from 'throttle-debounce';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { trackClick } from '@sola/cookies/components/datalayer.js';
import { LogoName } from '@sola/schema/lib/logos.js';
import { Link as LinkProps, NavigationProps } from '@sola/schema/lib/schema.js';
import { HeaderProps } from '@sola/schema/lib/skippy.js';

import { useNavigationHover } from '../../hooks/use-navigation-hover.js';
// TODO: Either centralize or find a way to reduce copy/paste of the AccountButton component
import { AccountButton } from '../../molecules/account-button/account-button.jsx';
import { NavBag } from '../../molecules/nav-bag/nav-bag.jsx';
import { NavSearch } from '../../molecules/nav-search/nav-search.jsx';
import { NavSheet } from '../../molecules/nav-sheet/nav-sheet.jsx';
import { NavStack } from '../../molecules/nav-stack/nav-stack.jsx';
import { getMenuItemColumns } from '../../utils/nav-utils.js';

type CustomizeProps = {
  logo?: LogoName;
  link?: LinkProps;
  showShoppingBag?: boolean;
  showLogin?: boolean;
  showSearch?: boolean;
};

export const bodyScrollLock = (
  isLocked: boolean,
  lastScrollPosition: number,
) => {
  if (!document || !lastScrollPosition) {
    return;
  }
  const { body } = document;
  if (isLocked) {
    body.style.position = 'fixed';
    body.style.top = `-${lastScrollPosition}px`;
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    return;
  }

  body.style.position = '';
  body.style.top = '';
  body.style.width = '';
  body.style.overflow = '';
  window.scrollTo(0, lastScrollPosition);
};

export const Header: React.FC<HeaderProps & CustomizeProps> = ({
  primaryMenu,
  logo = 'compassion',
  link,
  showShoppingBag,
  showLogin,
  showSearch,
}) => {
  const menuItems = primaryMenu.items || [];
  const { t } = useTranslation(['priority']);
  const [menuOpen, setMenuOpen] = useState('');
  const isMobileMenuOpen = menuOpen === 'mobile';
  const [navItems, setNavItems] = useState<NavigationProps[][]>([menuItems]);
  const pathname = usePathname();
  const [headerHidden, setHeaderHidden] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const mobileCloseButtonRef = useRef<HTMLButtonElement>(null);
  const mobileOpenButtonRef = useRef<HTMLButtonElement>(null);

  const {
    activeItem,
    setActiveItem,
    handleTriggerEnter,
    handleTriggerLeave,
    handleContentEnter,
    handleContentLeave,
    handleViewportEnter,
    handleViewportLeave,
    resetNavigation,
  } = useNavigationHover({
    switchDelay: 200,
    exitDelay: 400,
  });

  const throttledHandleScroll = useMemo(
    () =>
      throttle(250, () => {
        if (menuOpen === 'mobile' || activeItem) {
          return;
        }

        resetNavigation();

        const reappearTimeout = 5000; // 5 seconds
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // check > 80 to prevent whitespace behind the navbar appearing and mitigate rubberbanding on iOS
        if (scrollTop > lastScrollTopRef.current && scrollTop > 80) {
          setHeaderHidden(true);
        } else {
          setHeaderHidden(false);
        }

        lastScrollTopRef.current = scrollTop;

        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
          setHeaderHidden(false);
        }, reappearTimeout);
      }),
    [menuOpen, activeItem, resetNavigation],
  );

  useEffect(() => {
    if (isMobileMenuOpen) {
      const debouncedFocus = debounce(100, () => {
        mobileCloseButtonRef.current?.focus();
      });
      debouncedFocus();
      bodyScrollLock(true, lastScrollTopRef.current);
      return;
    }
    const debouncedFocus = debounce(200, () => {
      mobileOpenButtonRef.current?.focus();
    });
    debouncedFocus();
    bodyScrollLock(false, lastScrollTopRef.current);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current); // Cleanup timeout on unmount
      }
    };
  }, [throttledHandleScroll]);

  useEffect(() => {
    setMenuOpen('');
    resetNavigation();
  }, [pathname, resetNavigation]);

  useEffect(() => {
    if (menuOpen === 'mobile') {
      return;
    }
    if (activeItem) {
      setMenuOpen(activeItem);
      return;
    }
    setMenuOpen('');
  }, [activeItem, menuOpen]);

  return (
    <header
      role="banner"
      className={cn('Header', {
        'Header--hidden': headerHidden,
      })}
    >
      <ButtonStyle>
        <a className="Header__skip-link" href="#main">
          {t('header.skipLink')}
        </a>
      </ButtonStyle>
      <div
        className={cn('Header__container', {
          'is-mobile-open': !isMobileMenuOpen,
        })}
      >
        <div className="Header__menu-list-container w-full">
          <div className="Header__logo-container">
            <Link href={link?.link || '/'} title={t('header.logoAlt')}>
              <LogoChooser
                name={logo}
                className="Header__logo"
                color="blue"
                aria-labelledby="Header-logo-label"
                priority
              />
              <span id="Header-logo-label" className="sr-only">
                {t('header.logoAlt', { title: link?.title })}
              </span>
            </Link>
          </div>

          <NavigationMenu.Root
            aria-label="Main navigation"
            value={menuOpen}
            className="Header__root flex-col hidden lg:flex"
          >
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div>
                {/* desktop menu */}
                <ul className="Header__desktop-items xl:mx-roomy-xs">
                  {menuItems.map((item) => {
                    const hasChildren = Boolean(item.items?.length);
                    const columns = getMenuItemColumns(item);
                    return (
                      <NavigationMenu.Item
                        value={item.title}
                        key={item.title}
                        className="flex justify-center"
                      >
                        <NavigationMenu.Trigger
                          onTouchEnd={(e) => e.preventDefault()}
                          onPointerEnter={() =>
                            item.title && handleTriggerEnter(item.title)
                          }
                          onPointerLeave={handleTriggerLeave}
                          onFocus={() => {
                            if (menuOpen && item.title) {
                              handleTriggerEnter(item.title);
                            }
                          }}
                          onBlur={() => {
                            if (menuOpen) {
                              handleTriggerLeave();
                            }
                          }}
                          onClick={(e) => {
                            if (menuOpen && e.detail !== 0) {
                              e.preventDefault();
                            }
                            if (!item.title) {
                              return;
                            }
                            trackClick(
                              item.title,
                              item?.link || '#',
                              'Header__trigger',
                            );
                            // actions for screenreaders (tabs)
                            if (menuOpen === item.title) {
                              setActiveItem('');
                            } else {
                              setActiveItem(item.title);
                            }
                          }}
                          className={cn(
                            'Header__trigger flex items-center justify-between',
                            {
                              'Header__trigger--active':
                                menuOpen === item.title,
                            },
                          )}
                        >
                          {hasChildren ? (
                            <p className="label label-potent label-neutral flex items-center gap-snug-md">
                              {item.title}
                              <ArkIcon
                                iconName="chevron-down"
                                className="Header__chevron"
                              />
                            </p>
                          ) : (
                            <Link
                              href={item?.link || '#'}
                              title={item?.title}
                              target={item?.target}
                              className="Header__desktop-nav-item label label-potent label-neutral"
                            >
                              {item.title}
                            </Link>
                          )}
                        </NavigationMenu.Trigger>
                        {hasChildren && (
                          <NavigationMenu.Content
                            className="Header__content Header__container flex justify-center"
                            onPointerEnter={handleContentEnter}
                            onPointerLeave={handleContentLeave}
                            onFocus={handleContentEnter}
                            onBlur={handleContentLeave}
                          >
                            <div className="w-full max-w-xl flex flex-col">
                              <div className="pt-roomy-xs pb-comfy-lg">
                                <div className="Header__link-container pb-snug-md">
                                  <LinkStyle
                                    noPadding
                                    noUnderline
                                    size="lg"
                                    iconAfter="arrow-right"
                                  >
                                    <Link
                                      href={item?.link || '#'}
                                      target={item?.target}
                                      title={item?.title}
                                    >
                                      {item.title}
                                    </Link>
                                  </LinkStyle>
                                </div>
                              </div>
                              <div className="Header__navsheet-container gap-comfy-xl pb-comfy-xl pt-comfy-xs">
                                {columns.map((col) => (
                                  <NavSheet
                                    key={col.map((ci) => ci.title).join()}
                                    items={col}
                                  />
                                ))}
                              </div>
                            </div>
                          </NavigationMenu.Content>
                        )}
                      </NavigationMenu.Item>
                    );
                  })}
                </ul>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport
                className="Header__menu-viewport flex justify-center"
                onPointerEnter={handleViewportEnter}
                onPointerLeave={handleViewportLeave}
                onFocus={handleViewportEnter}
                onBlur={handleViewportLeave}
              />
            </div>
          </NavigationMenu.Root>

          <NavigationMenu.Root
            onValueChange={(value) => {
              if (value === 'mobile') {
                setMenuOpen(value);
                resetNavigation();
              }
            }}
            value={menuOpen}
            className="Header__root flex flex-col"
            aria-label="Action navigation"
          >
            <NavigationMenu.List className="Header__menu-list" asChild>
              <div className="Header__desktop-actions flex items-center gap-comfy-sm justify-end">
                <div className="flex flex-row gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                  <div className="flex gap-snug-md md:gap-snug-xl">
                    {showSearch && <NavSearch />}
                    {showShoppingBag && <NavBag />}
                  </div>
                  {showLogin && <AccountButton />}

                  {/* mobile menu */}
                  <NavigationMenu.Item
                    className="Header__nav-action Header__mobile-menu-trigger flex lg:hidden items-center"
                    key="mobile hamburger menu"
                    value="mobile"
                    asChild
                  >
                    <div>
                      <NavigationMenu.Trigger
                        onPointerEnter={(e) => e.stopPropagation()}
                        onPointerMove={(e) => e.preventDefault()}
                        onPointerLeave={(e) => e.preventDefault()}
                        onClick={() => {
                          trackClick(t('header.menu'));
                        }}
                        className="flex items-center p-comfy-xs rounded-full"
                        title={t('header.menu')}
                      >
                        <ArkIcon
                          iconName="menu-01"
                          className="Header__nav-icon"
                        />
                      </NavigationMenu.Trigger>
                      <NavigationMenu.Content
                        className="Header__content Header__content--mobile flex flex-col"
                        onPointerLeave={(e) => e.preventDefault()}
                      >
                        <div
                          className={cn(
                            'Header__mobile-nav flex px-comfy-xs py-comfy-lg items-center',
                            {
                              'justify-end': navItems.length === 1,
                              'justify-between': navItems.length > 1,
                            },
                          )}
                        >
                          {Boolean(navItems.length > 1) && (
                            <button
                              ref={mobileOpenButtonRef}
                              className="flex items-center"
                              type="button"
                              onClick={() => {
                                setNavItems(navItems.slice(0, -1));
                              }}
                            >
                              <ArkIcon
                                iconName="arrow-left"
                                className="Header__nav-icon"
                              />
                            </button>
                          )}

                          <span className="flex items-center gap-snug-md md:gap-snug-xl lg:gap-comfy-sm">
                            {showSearch && <NavSearch />}
                            {showShoppingBag && <NavBag />}
                            {showLogin && <AccountButton simple />}
                            <button
                              ref={mobileCloseButtonRef}
                              className="Header__nav-action flex items-center p-comfy-xs"
                              type="button"
                              title={t('header.menu')}
                              onClick={() => {
                                setMenuOpen('');
                                setNavItems([menuItems]);
                                resetNavigation();
                              }}
                            >
                              <ArkIcon
                                iconName="x-close"
                                className="Header__nav-icon"
                              />
                            </button>
                          </span>
                        </div>
                        <NavStack
                          items={navItems}
                          setItems={setNavItems}
                          showMyAccountSection={showLogin}
                          className="pb-roomy-sm"
                        />
                      </NavigationMenu.Content>
                    </div>
                  </NavigationMenu.Item>
                </div>
              </div>
            </NavigationMenu.List>

            {/* The entering animation seems to like having this here. Not sure why. */}
            <NavigationMenu.NavigationMenuIndicator />

            <div className="Header__viewport-position">
              <NavigationMenu.Viewport
                className="Header__menu-viewport flex justify-center"
                onPointerEnter={handleViewportEnter}
                onPointerLeave={handleViewportLeave}
              />
            </div>
          </NavigationMenu.Root>
        </div>
      </div>
    </header>
  );
};

export default Header;
